import { useCallback, useMemo, useRef, useState } from 'react'

import { UNDO_STACK_LIMIT, type ExplorerUndoEntry } from './undo'

export interface ExplorerUndo {
  entries: readonly ExplorerUndoEntry[]
  push: (entry: ExplorerUndoEntry) => void
  pop: () => ExplorerUndoEntry | undefined
  clear: () => void
}

export function useExplorerUndo(limit = UNDO_STACK_LIMIT): ExplorerUndo {
  const [entries, setEntries] = useState<ExplorerUndoEntry[]>([])
  const entriesRef = useRef(entries)
  const replace = useCallback((next: ExplorerUndoEntry[]) => {
    entriesRef.current = next
    setEntries(next)
  }, [])

  const push = useCallback(
    (entry: ExplorerUndoEntry) => {
      replace([...entriesRef.current, entry].slice(-limit))
    },
    [limit, replace]
  )
  const pop = useCallback((): ExplorerUndoEntry | undefined => {
    const current = entriesRef.current
    if (current.length === 0) return undefined
    const entry = current[current.length - 1]
    replace(current.slice(0, -1))
    return entry
  }, [replace])
  const clear = useCallback(() => {
    replace([])
  }, [replace])

  return useMemo(() => ({ entries, push, pop, clear }), [clear, entries, pop, push])
}
